import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`bg-[#09090B] rounded-2xl border border-white/10 shadow-2xl w-full ${sizeClasses[size]} relative z-10 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <h3 className="font-bold text-lg text-white">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  );
};

// Team Member Account Access Modal Component
const TeamMemberAccountsModal = ({ isOpen, onClose, member, addToast, onUpdate }) => {
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');

  // Fetch all accounts and member's current assignments
  useEffect(() => {
    if (isOpen && member) {
      const fetchData = async () => {
        setLoading(true);
        try {
          // Fetch all accounts
          const accountsRes = await fetch(`${API_URL}/accounts/all`);
          const accountsData = await accountsRes.json();
          setAllAccounts(accountsData.accounts || []);
          
          // Fetch member's current assigned accounts
          const accessRes = await fetch(`${API_URL}/team/${member.id}/accounts`);
          const accessData = await accessRes.json();
          setSelectedAccountIds(new Set(accessData.account_ids || []));
        } catch (error) {
          console.error('Error fetching data:', error);
          if (addToast) addToast('Failed to load accounts', 'error');
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [isOpen, member, addToast]);

  // Filter accounts based on search and platform
  const filteredAccounts = useMemo(() => {
    return allAccounts.filter(acc => {
      const matchesSearch = acc.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPlatform = platformFilter === 'all' || acc.platform === platformFilter;
      return matchesSearch && matchesPlatform;
    });
  }, [allAccounts, searchTerm, platformFilter]);

  // Group accounts by platform
  const groupedAccounts = useMemo(() => {
    const groups = { META: [], GOOGLE: [] };
    filteredAccounts.forEach(acc => {
      if (groups[acc.platform]) {
        groups[acc.platform].push(acc);
      }
    });
    return groups;
  }, [filteredAccounts]);

  // Toggle account selection
  const toggleAccount = (accountId) => {
    setSelectedAccountIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  // Select/Deselect all visible accounts
  const toggleAll = (select) => {
    if (select) {
      setSelectedAccountIds(prev => {
        const newSet = new Set(prev);
        filteredAccounts.forEach(acc => newSet.add(acc.id));
        return newSet;
      });
    } else {
      setSelectedAccountIds(prev => {
        const newSet = new Set(prev);
        filteredAccounts.forEach(acc => newSet.delete(acc.id));
        return newSet;
      });
    }
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/team/${member.id}/accounts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_ids: Array.from(selectedAccountIds) })
      });
      
      if (response.ok) {
        if (addToast) addToast('Account assignments updated successfully', 'success');
        onUpdate();
        onClose();
      } else {
        const error = await response.json();
        if (addToast) addToast(error.detail || 'Failed to update assignments', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to update assignments', 'error');
    }
    setSaving(false);
  };

  const metaCount = groupedAccounts.META.length;
  const googleCount = groupedAccounts.GOOGLE.length;
  const selectedMetaCount = groupedAccounts.META.filter(a => selectedAccountIds.has(a.id)).length;
  const selectedGoogleCount = groupedAccounts.GOOGLE.filter(a => selectedAccountIds.has(a.id)).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Manage Accounts - ${member?.name}`} size="lg">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search accounts..."
                className="w-full pl-10 pr-4 py-2.5 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-500 outline-none focus:border-[#6366f1] text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPlatformFilter('all')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  platformFilter === 'all' 
                    ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30' 
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setPlatformFilter('META')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  platformFilter === 'META' 
                    ? 'bg-[#1877F2]/20 text-[#1877F2] border border-[#1877F2]/30' 
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                Meta ({metaCount})
              </button>
              <button
                onClick={() => setPlatformFilter('GOOGLE')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  platformFilter === 'GOOGLE' 
                    ? 'bg-[#4285F4]/20 text-[#4285F4] border border-[#4285F4]/30' 
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                Google ({googleCount})
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-400">
              <span className="text-[#22c55e] font-bold">{selectedAccountIds.size}</span> accounts selected
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => toggleAll(true)}
                className="px-3 py-1.5 text-xs font-medium text-[#6366f1] hover:bg-[#6366f1]/10 rounded-lg transition-colors"
              >
                Select All Visible
              </button>
              <button
                onClick={() => toggleAll(false)}
                className="px-3 py-1.5 text-xs font-medium text-zinc-400 hover:bg-white/10 rounded-lg transition-colors"
              >
                Deselect All Visible
              </button>
            </div>
          </div>

          {/* Account List */}
          <div className="max-h-[400px] overflow-y-auto space-y-4 pr-1">
            {/* Meta Accounts */}
            {groupedAccounts.META.length > 0 && (platformFilter === 'all' || platformFilter === 'META') && (
              <div>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#09090B] py-1">
                  <div className="w-6 h-6 rounded bg-[#1877F2]/20 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-[#1877F2]">M</span>
                  </div>
                  <h4 className="text-sm font-semibold text-white">Meta Accounts</h4>
                  <span className="text-xs text-zinc-500">({selectedMetaCount}/{metaCount} selected)</span>
                </div>
                <div className="grid gap-2">
                  {groupedAccounts.META.map(account => (
                    <label
                      key={account.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        selectedAccountIds.has(account.id)
                          ? 'bg-[#1877F2]/10 border-[#1877F2]/30'
                          : 'bg-[#18181B] border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccountIds.has(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        className="w-4 h-4 rounded border-white/20 bg-[#18181B] text-[#1877F2] focus:ring-[#1877F2]/20"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{account.name}</p>
                        <p className="text-xs text-zinc-500">{account.account_id}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        account.status === 'ACTIVE' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {account.status || 'N/A'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Google Accounts */}
            {groupedAccounts.GOOGLE.length > 0 && (platformFilter === 'all' || platformFilter === 'GOOGLE') && (
              <div>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#09090B] py-1">
                  <div className="w-6 h-6 rounded bg-[#4285F4]/20 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-[#4285F4]">G</span>
                  </div>
                  <h4 className="text-sm font-semibold text-white">Google Accounts</h4>
                  <span className="text-xs text-zinc-500">({selectedGoogleCount}/{googleCount} selected)</span>
                </div>
                <div className="grid gap-2">
                  {groupedAccounts.GOOGLE.map(account => (
                    <label
                      key={account.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        selectedAccountIds.has(account.id)
                          ? 'bg-[#4285F4]/10 border-[#4285F4]/30'
                          : 'bg-[#18181B] border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccountIds.has(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        className="w-4 h-4 rounded border-white/20 bg-[#18181B] text-[#4285F4] focus:ring-[#4285F4]/20"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{account.name}</p>
                        <p className="text-xs text-zinc-500">{account.account_id}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        account.status === 'ENABLED' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {account.status || 'N/A'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {filteredAccounts.length === 0 && (
              <div className="text-center py-8">
                <p className="text-zinc-500 text-sm">No accounts found matching your search</p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Assignments
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const TeamManagement = ({ teamMembers, onRefresh, addToast }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'marketer', phone: '' });
  const [saving, setSaving] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberAccounts, setMemberAccounts] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState({});
  
  // Account management modal state
  const [accountsModalMember, setAccountsModalMember] = useState(null);
  
  // Unassigned accounts
  const [unassignedAccounts, setUnassignedAccounts] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [assigningAccount, setAssigningAccount] = useState(null);
  
  // Login Management State  
  const [activeView, setActiveView] = useState('team'); // 'team' | 'logins'
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'user' });
  const [editingUser, setEditingUser] = useState(null);
  const [userPassword, setUserPassword] = useState({});

  // Fetch accounts for a specific team member
  const fetchMemberAccounts = useCallback(async (memberId) => {
    setLoadingAccounts(prev => ({ ...prev, [memberId]: true }));
    try {
      const response = await fetch(`${API_URL}/team/accounts?assigned_to=${memberId}`);
      const data = await response.json();
      setMemberAccounts(prev => ({ ...prev, [memberId]: data.accounts || [] }));
    } catch (error) {
      addToast('Failed to load accounts', 'error');
    }
    setLoadingAccounts(prev => ({ ...prev, [memberId]: false }));
  }, [addToast]);

  // Fetch unassigned accounts
  const fetchUnassignedAccounts = useCallback(async () => {
    setLoadingUnassigned(true);
    try {
      const response = await fetch(`${API_URL}/team/accounts?unassigned=true`);
      const data = await response.json();
      setUnassignedAccounts(data.accounts || []);
    } catch (error) {
      addToast('Failed to load unassigned accounts', 'error');
    }
    setLoadingUnassigned(false);
  }, [addToast]);

  // Fetch admin users
  const fetchAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/admin-users`);
      const data = await response.json();
      setAdminUsers(data.users || []);
    } catch (error) {
      addToast('Failed to load admin users', 'error');
    }
    setLoadingUsers(false);
  };

  useEffect(() => {
    if (activeView === 'logins') {
      fetchAdminUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  useEffect(() => {
    if (showUnassigned) {
      fetchUnassignedAccounts();
    }
  }, [showUnassigned, fetchUnassignedAccounts]);

  // Expand member and fetch their accounts
  const handleExpandMember = (memberId) => {
    if (expandedMember === memberId) {
      setExpandedMember(null);
    } else {
      setExpandedMember(memberId);
      if (!memberAccounts[memberId]) {
        fetchMemberAccounts(memberId);
      }
    }
  };

  // Assign account to member
  const handleAssignAccount = async (accountId, memberId) => {
    setAssigningAccount(accountId);
    try {
      await fetch(`${API_URL}/accounts/${accountId}/assignment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_marketer_id: memberId })
      });
      addToast('Account assigned successfully', 'success');
      
      // Refresh unassigned and member accounts
      fetchUnassignedAccounts();
      if (memberId) {
        fetchMemberAccounts(memberId);
      }
    } catch (error) {
      addToast('Failed to assign account', 'error');
    }
    setAssigningAccount(null);
  };

  // Unassign account from member
  const handleUnassignAccount = async (accountId, memberId) => {
    setAssigningAccount(accountId);
    try {
      await fetch(`${API_URL}/accounts/${accountId}/assignment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_marketer_id: null })
      });
      addToast('Account unassigned', 'success');
      
      // Refresh member accounts
      fetchMemberAccounts(memberId);
      if (showUnassigned) {
        fetchUnassignedAccounts();
      }
    } catch (error) {
      addToast('Failed to unassign account', 'error');
    }
    setAssigningAccount(null);
  };

  const handleAddMember = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newMember)
      });
      if (response.ok) {
        addToast('Team member added', 'success');
        setShowAddModal(false);
        setNewMember({ name: '', email: '', role: 'marketer', phone: '' });
        onRefresh();
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to add member', 'error');
      }
    } catch (error) {
      addToast('Failed to add team member', 'error');
    }
    setSaving(false);
  };

  const handleUpdateMember = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/team/${editingMember.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingMember)
      });
      addToast('Team member updated', 'success');
      setEditingMember(null);
      onRefresh();
    } catch (error) {
      addToast('Failed to update member', 'error');
    }
    setSaving(false);
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await fetch(`${API_URL}/team/${memberId}`, { method: 'DELETE' });
      addToast('Team member removed', 'success');
      onRefresh();
    } catch (error) {
      addToast('Failed to remove member', 'error');
    }
  };

  // Admin user functions
  const handleAddUser = async () => {
    if (!newUser.email.endsWith('@profitcast.com')) {
      addToast('Email must be @profitcast.com', 'error');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      if (response.ok) {
        addToast('User added successfully', 'success');
        setShowAddUserModal(false);
        setNewUser({ email: '', password: '', name: '', role: 'user' });
        fetchAdminUsers();
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to add user', 'error');
      }
    } catch (error) {
      addToast('Failed to add user', 'error');
    }
    setSaving(false);
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/admin-users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingUser.name, role: editingUser.role })
      });
      addToast('User updated', 'success');
      setEditingUser(null);
      fetchAdminUsers();
    } catch (error) {
      addToast('Failed to update user', 'error');
    }
    setSaving(false);
  };

  const handleSetUserPassword = async (userId) => {
    const pwd = userPassword[userId];
    if (!pwd || pwd.length < 4) {
      addToast('Password must be at least 4 characters', 'error');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/admin-users/${userId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd })
      });
      if (response.ok) {
        addToast('Password updated', 'success');
        setUserPassword(prev => ({ ...prev, [userId]: '' }));
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to update password', 'error');
      }
    } catch (error) {
      addToast('Failed to update password', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await fetch(`${API_URL}/admin-users/${userId}`, { method: 'DELETE' });
      addToast('User deleted', 'success');
      fetchAdminUsers();
    } catch (error) {
      addToast('Failed to delete user', 'error');
    }
  };

  const getRoleConfig = (role) => {
    const configs = {
      admin: { bg: 'bg-[#22c55e]/10', text: 'text-[#22c55e]', ring: 'ring-[#22c55e]/30', label: 'Admin' },
      manager: { bg: 'bg-[#6366f1]/10', text: 'text-[#6366f1]', ring: 'ring-[#6366f1]/30', label: 'Manager' },
      marketer: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', ring: 'ring-zinc-500/30', label: 'Marketer' }
    };
    return configs[role] || configs.marketer;
  };

  const getInitialsGradient = (name) => {
    const gradients = [
      'from-[#6366f1] to-[#22c55e]',
      'from-purple-500 to-pink-500',
      'from-orange-500 to-red-500',
      'from-blue-500 to-cyan-500',
      'from-green-500 to-emerald-500',
    ];
    const index = name?.charCodeAt(0) % gradients.length || 0;
    return gradients[index];
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Team Management</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage team members and their account assignments</p>
        </div>
        <div className="flex items-center gap-3">
          {activeView === 'team' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2.5 bg-[#6366f1] text-white rounded-xl font-semibold hover:bg-[#5558e8] transition-colors shadow-lg shadow-[#6366f1]/20 text-sm flex items-center gap-2"
              data-testid="add-member-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Member
            </button>
          )}
          {activeView === 'logins' && (
            <button
              onClick={() => setShowAddUserModal(true)}
              className="px-4 py-2.5 bg-[#6366f1] text-white rounded-xl font-semibold hover:bg-[#5558e8] transition-colors shadow-lg shadow-[#6366f1]/20 text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add User
            </button>
          )}
        </div>
      </div>

      {/* View Toggle - Simplified to 2 tabs */}
      <div className="flex items-center gap-1 p-1 bg-[#09090B] rounded-xl border border-white/10 w-fit">
        <button
          onClick={() => setActiveView('team')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeView === 'team' 
              ? 'bg-[#6366f1] text-white' 
              : 'text-zinc-400 hover:text-white'
          }`}
          data-testid="team-tab"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Team & Accounts
        </button>
        <button
          onClick={() => setActiveView('logins')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeView === 'logins' 
              ? 'bg-[#6366f1] text-white' 
              : 'text-zinc-400 hover:text-white'
          }`}
          data-testid="logins-tab"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          Dashboard Logins
        </button>
      </div>

      {/* Team & Accounts View */}
      {activeView === 'team' && (
        <div className="space-y-6">
          {/* Team Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-[#22c55e]/10 to-transparent border border-[#22c55e]/20 rounded-xl p-4">
              <p className="text-2xl font-bold text-[#22c55e] font-mono">{teamMembers.filter(m => m.role === 'admin').length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Admins</p>
            </div>
            <div className="bg-gradient-to-br from-[#6366f1]/10 to-transparent border border-[#6366f1]/20 rounded-xl p-4">
              <p className="text-2xl font-bold text-[#6366f1] font-mono">{teamMembers.filter(m => m.role === 'manager').length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Managers</p>
            </div>
            <div className="bg-[#18181B] border border-white/10 rounded-xl p-4">
              <p className="text-2xl font-bold text-white font-mono">{teamMembers.filter(m => m.role === 'marketer').length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Marketers</p>
            </div>
            <div 
              className={`border rounded-xl p-4 cursor-pointer transition-colors ${
                showUnassigned 
                  ? 'bg-amber-500/20 border-amber-500/30' 
                  : 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/15'
              }`}
              onClick={() => setShowUnassigned(!showUnassigned)}
            >
              <p className="text-2xl font-bold text-amber-400 font-mono">{unassignedAccounts.length || '...'}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Unassigned</p>
            </div>
          </div>

          {/* Unassigned Accounts Section */}
          {showUnassigned && (
            <div className="bg-[#09090B] rounded-2xl border border-amber-500/20 overflow-hidden animate-fade-in">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Unassigned Accounts</h3>
                    <p className="text-xs text-zinc-500">{unassignedAccounts.length} accounts need assignment</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUnassigned(false)}
                  className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 max-h-[300px] overflow-y-auto">
                {loadingUnassigned ? (
                  <div className="text-center py-8">
                    <svg className="animate-spin w-6 h-6 mx-auto text-amber-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  </div>
                ) : unassignedAccounts.length === 0 ? (
                  <p className="text-center text-zinc-500 py-8">All accounts are assigned!</p>
                ) : (
                  <div className="space-y-2">
                    {unassignedAccounts.slice(0, 10).map(account => (
                      <div key={account.id} className="flex items-center justify-between p-3 bg-[#18181B] rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            account.platform === 'META' ? 'bg-[#1877F2]/20' : 'bg-[#4285F4]/20'
                          }`}>
                            {account.platform === 'META' ? (
                              <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white text-sm">{account.name}</p>
                            <p className="text-xs text-zinc-500">{formatCurrency(account.spend)}</p>
                          </div>
                        </div>
                        <select
                          onChange={(e) => e.target.value && handleAssignAccount(account.id, e.target.value)}
                          disabled={assigningAccount === account.id}
                          className="px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-lg text-sm text-amber-400 focus:ring-2 focus:ring-amber-500/50 outline-none cursor-pointer"
                          defaultValue=""
                        >
                          <option value="" disabled>Assign to...</option>
                          {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>
                    ))}
                    {unassignedAccounts.length > 10 && (
                      <p className="text-center text-zinc-500 text-sm py-2">
                        +{unassignedAccounts.length - 10} more accounts
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Search + Filter */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              placeholder="Search by name or role..."
              className="w-full bg-[#09090B] border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
            />
          </div>

          {/* Team Members with Accounts */}
          <div className="space-y-4">
            {teamMembers.filter(m =>
              !memberSearch ||
              m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
              m.role.toLowerCase().includes(memberSearch.toLowerCase())
            ).map(member => {
              const config = getRoleConfig(member.role);
              const isExpanded = expandedMember === member.id;
              const accounts = memberAccounts[member.id] || [];
              const isLoading = loadingAccounts[member.id];

              return (
                <div 
                  key={member.id} 
                  className="bg-[#09090B] rounded-2xl border border-white/10 overflow-hidden transition-colors"
                  data-testid={`member-card-${member.id}`}
                >
                  {/* Member Header */}
                  <div 
                    className="p-5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                    onClick={() => handleExpandMember(member.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 bg-gradient-to-br ${getInitialsGradient(member.name)} rounded-xl flex items-center justify-center text-black font-bold shadow-lg`}>
                          {member.name.charAt(0)}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-3">
                            <h3 className="font-bold text-white">{member.name}</h3>
                            <span className={`${config.bg} ${config.text} px-2 py-0.5 rounded-md text-xs font-semibold`}>
                              {config.label}
                            </span>
                          </div>
                          <span className="text-zinc-400 text-xs font-mono mt-1 truncate">{member.email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right mr-4">
                          <p className="text-xl font-bold text-[#6366f1] font-mono">{accounts.length || '...'}</p>
                          <p className="text-xs text-zinc-500">Accounts</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Manage Accounts Button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setAccountsModalMember(member); }}
                            className="px-3 py-2 rounded-lg bg-[#6366f1]/10 border border-[#6366f1]/30 text-[#6366f1] text-xs font-semibold hover:bg-[#6366f1]/20 transition-colors flex items-center gap-1.5"
                            data-testid={`manage-accounts-${member.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            Accounts
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingMember(member); }}
                            className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                            data-testid={`edit-member-${member.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteMember(member.id); }}
                            className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors"
                            data-testid={`delete-member-${member.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Accounts Section */}
                  {isExpanded && (
                    <div className="border-t border-white/10 bg-[#0D0D0F]">
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-zinc-400">Assigned Accounts</p>
                          <button
                            onClick={() => fetchMemberAccounts(member.id)}
                            className="text-xs text-[#6366f1] hover:text-[#22c55e] transition-colors flex items-center gap-1"
                          >
                            <svg className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh
                          </button>
                        </div>

                        {isLoading ? (
                          <div className="text-center py-6">
                            <svg className="animate-spin w-6 h-6 mx-auto text-[#6366f1]" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                          </div>
                        ) : accounts.length === 0 ? (
                          <div className="text-center py-6 bg-[#18181B] rounded-xl">
                            <p className="text-zinc-500 text-sm">No accounts assigned</p>
                            <p className="text-xs text-zinc-600 mt-1">Click on unassigned accounts above to assign</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {accounts.map(account => (
                              <div 
                                key={account.id} 
                                className="flex items-center justify-between p-3 bg-[#18181B] rounded-xl group hover:bg-[#1a1a1d] transition-colors"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
                                    account.platform === 'META' ? 'bg-[#1877F2]/20' : 'bg-[#4285F4]/20'
                                  }`}>
                                    {account.platform === 'META' ? (
                                      <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-white text-sm truncate">{account.name}</p>
                                    <p className="text-xs text-zinc-500">{formatCurrency(account.spend)}</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleUnassignAccount(account.id, member.id)}
                                  disabled={assigningAccount === account.id}
                                  className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors flex-shrink-0"
                                  title="Unassign"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {teamMembers.length === 0 && (
              <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                  <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">No Team Members Yet</h3>
                <p className="text-zinc-500 mb-6">Add your first team member to get started.</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-6 py-3 bg-[#6366f1] text-white rounded-xl text-sm font-bold hover:bg-[#5558e8] transition-colors shadow-lg shadow-[#6366f1]/20"
                >
                  Add Team Member
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Login Management View */}
      {activeView === 'logins' && (
        <div className="space-y-6">
          <div className="bg-[#09090B] rounded-2xl p-4 border border-white/10 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#6366f1]/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-zinc-400">
                Manage dashboard login credentials. Only <span className="text-[#22c55e] font-semibold">@profitcast.com</span> emails allowed.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-[#22c55e]/10 to-transparent border border-[#22c55e]/20 rounded-2xl p-5">
              <p className="text-3xl font-bold text-[#22c55e] font-mono">{adminUsers.filter(u => u.role === 'admin').length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Admins</p>
            </div>
            <div className="bg-[#18181B] border border-white/10 rounded-2xl p-5">
              <p className="text-3xl font-bold text-white font-mono">{adminUsers.filter(u => u.role === 'user').length}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Users</p>
            </div>
          </div>

          {loadingUsers ? (
            <div className="text-center py-12">
              <svg className="animate-spin w-8 h-8 mx-auto mb-3 text-[#6366f1]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            </div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
              <h3 className="text-lg font-bold text-white mb-2">No Login Users Yet</h3>
              <p className="text-zinc-500 mb-6">Add @profitcast.com users who can login.</p>
              <button
                onClick={() => setShowAddUserModal(true)}
                className="px-6 py-3 bg-[#6366f1] text-white rounded-xl text-sm font-bold"
              >
                Add First User
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {adminUsers.map(user => (
                <div key={user.id} className="bg-[#09090B] rounded-2xl border border-white/10 p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        user.role === 'admin' ? 'bg-gradient-to-br from-[#22c55e]/30 to-[#6366f1]/30' : 'bg-gradient-to-br from-zinc-700 to-zinc-800'
                      }`}>
                        <span className="text-xl font-bold text-white">{user.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{user.name}</h3>
                        <p className="text-xs text-zinc-500 font-mono">{user.email}</p>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase ${
                      user.role === 'admin' ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-zinc-500/10 text-zinc-400'
                    }`}>
                      {user.role}
                    </span>
                  </div>

                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Set Password</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={userPassword[user.id] || ''}
                        onChange={e => setUserPassword(prev => ({ ...prev, [user.id]: e.target.value }))}
                        placeholder="Enter new password"
                        className="flex-1 px-3 py-2 bg-[#18181B] border border-white/10 rounded-lg text-sm text-white placeholder-zinc-500 outline-none focus:border-[#6366f1]"
                      />
                      <button
                        onClick={() => handleSetUserPassword(user.id)}
                        disabled={!userPassword[user.id]}
                        className="px-4 py-2 bg-[#6366f1] text-white rounded-lg text-sm font-bold disabled:opacity-50"
                      >
                        Set
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
                    <button
                      onClick={() => setEditingUser(user)}
                      className="flex-1 px-4 py-2 bg-white/5 text-zinc-400 rounded-xl text-sm font-medium hover:bg-white/10 hover:text-white transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="px-4 py-2 bg-red-500/10 text-red-400 rounded-xl text-sm font-medium hover:bg-red-500/20 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-[#09090B] rounded-2xl p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-6">Add Team Member</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Name</label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-600 outline-none focus:border-[#6366f1]"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Email</label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={e => setNewMember({ ...newMember, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-600 outline-none focus:border-[#6366f1]"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Role</label>
                <select
                  value={newMember.role}
                  onChange={e => setNewMember({ ...newMember, role: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white outline-none focus:border-[#6366f1] cursor-pointer"
                >
                  <option value="marketer">Marketer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 bg-white/5 text-zinc-400 rounded-xl font-semibold hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={saving || !newMember.name || !newMember.email}
                className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-semibold hover:bg-[#5558e8] transition-colors disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditingMember(null)}>
          <div className="bg-[#09090B] rounded-2xl p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-6">Edit Team Member</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Name</label>
                <input
                  type="text"
                  value={editingMember.name}
                  onChange={e => setEditingMember({ ...editingMember, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white outline-none focus:border-[#6366f1]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Email</label>
                <input type="email" value={editingMember.email} disabled className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-zinc-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Role</label>
                <select
                  value={editingMember.role}
                  onChange={e => setEditingMember({ ...editingMember, role: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white outline-none focus:border-[#6366f1] cursor-pointer"
                >
                  <option value="marketer">Marketer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setEditingMember(null)} className="flex-1 px-4 py-3 bg-white/5 text-zinc-400 rounded-xl font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={handleUpdateMember} disabled={saving} className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-semibold hover:bg-[#5558e8] transition-colors disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddUserModal(false)}>
          <div className="bg-[#09090B] rounded-2xl p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-6">Add Login User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-600 outline-none focus:border-[#6366f1]"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-600 outline-none focus:border-[#6366f1]"
                  placeholder="user@profitcast.com"
                />
                {newUser.email && !newUser.email.endsWith('@profitcast.com') && (
                  <p className="text-xs text-red-400 mt-1">Email must end with @profitcast.com</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Password</label>
                <input
                  type="text"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-600 outline-none focus:border-[#6366f1]"
                  placeholder="Min 4 characters"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Role</label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white outline-none focus:border-[#6366f1] cursor-pointer"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowAddUserModal(false)} className="flex-1 px-4 py-3 bg-white/5 text-zinc-400 rounded-xl font-semibold hover:bg-white/10">Cancel</button>
              <button
                onClick={handleAddUser}
                disabled={saving || !newUser.name || !newUser.email || !newUser.password || !newUser.email.endsWith('@profitcast.com')}
                className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-semibold hover:bg-[#5558e8] transition-colors disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-[#09090B] rounded-2xl p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-6">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Name</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white outline-none focus:border-[#6366f1]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-2 uppercase">Role</label>
                <select
                  value={editingUser.role}
                  onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white outline-none focus:border-[#6366f1] cursor-pointer"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setEditingUser(null)} className="flex-1 px-4 py-3 bg-white/5 text-zinc-400 rounded-xl font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={handleUpdateUser} disabled={saving} className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-semibold hover:bg-[#5558e8] transition-colors disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Member Accounts Modal */}
      <TeamMemberAccountsModal
        isOpen={!!accountsModalMember}
        onClose={() => setAccountsModalMember(null)}
        member={accountsModalMember}
        addToast={addToast}
        onUpdate={() => {
          // Refresh member accounts and unassigned accounts
          if (accountsModalMember) {
            fetchMemberAccounts(accountsModalMember.id);
          }
          fetchUnassignedAccounts();
          onRefresh();
        }}
      />
    </div>
  );
};

export default TeamManagement;
